import {
  Box,
  Typography,
  Chip,
  Button,
  TextField
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

/**
 * Dosya boyutunu formatla
 */
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Dosya/görsel önizleme kontrolü
 */
const isImageFile = (fileName = '', mimeType = '') => {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff'];
  const ext = fileName ? fileName.toLowerCase().slice(fileName.lastIndexOf('.')) : '';
  return mimeType.startsWith('image/') || imageExtensions.includes(ext);
};

/**
 * Dosya gösterimi bileşeni
 */
const FileDisplay = ({ file, onDelete, showDeleteButton = false }) => {
  if (!file) return null;

  const renderFileContent = (fileData) => {
    const fileName = typeof fileData === 'object' ? fileData.name : String(fileData);
    const fileUrl = typeof fileData === 'object' ? fileData.base64 : (
      typeof fileData === 'string' && (fileData.startsWith('data:') || fileData.startsWith('http') || fileData.startsWith('blob:')) ? fileData : null
    );
    const mimeType = typeof fileData === 'object' ? fileData.type : '';
    const fileSize = typeof fileData === 'object' ? fileData.size : 0;
    const isImage = isImageFile(fileName, mimeType);

    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        bgcolor: '#f5f5f5',
        width: '100%',
        justifyContent: 'space-between',
        mb: 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          bgcolor: '#eeeeee',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isImage && fileUrl ? (
            <Box sx={{ position: 'relative' }}>
              <Box 
                component="img" 
                src={fileUrl} 
                alt={fileName} 
                sx={{ 
                  width: 64, 
                  height: 64, 
                  objectFit: 'cover', 
                  borderRadius: 1, 
                  border: '1px solid #ccc',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
                onClick={() => {
                  const newWindow = window.open();
                  newWindow.document.write(`
                    <html>
                      <head>
                        <title>${fileName}</title>
                        <style>
                          body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f5f5f5; }
                          img { max-width: 90%; max-height: 90vh; object-fit: contain; }
                        </style>
                      </head>
                      <body>
                        <img src="${fileUrl}" alt="${fileName}" />
                      </body>
                    </html>
                  `);
                }}
              />
            </Box>
          ) : (
            <Box sx={{ 
              width: 64, 
              height: 64, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              bgcolor: '#e3f2fd',
              borderRadius: 1,
              border: '1px solid #90caf9'
            }}>
              <AttachFileIcon color="primary" sx={{ fontSize: 32 }} />
            </Box>
          )}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {fileName || 'Dosya'}
            </Typography>
            {fileSize > 0 && (
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(fileSize)}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {fileUrl && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.download = fileName || 'download';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              startIcon={<AttachFileIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 2
              }}
            >
              İndir
            </Button>
          )}
          {showDeleteButton && onDelete && (
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={onDelete}
              startIcon={<DeleteIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 2
              }}
            >
              Sil
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  if (Array.isArray(file)) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 1,
        maxHeight: '400px',
        overflowY: 'auto',
        p: 1
      }}>
        {file.map((f, idx) => (
          <Box key={idx}>{renderFileContent(f)}</Box>
        ))}
      </Box>
    );
  }

  return renderFileContent(file);
};

/**
 * İmza gösterimi bileşeni
 */
const SignatureDisplay = ({ signature, onDelete, showDeleteButton = false }) => {
  if (!signature) return null;

  const signatureUrl = typeof signature === 'object' ? signature.base64 : signature;

  return (
    <Box sx={{ 
      border: '1px solid #e0e0e0',
      borderRadius: 1,
      p: 2,
      bgcolor: '#f5f5f5'
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 1 
      }}>
        <Typography variant="subtitle2">
          İmza
        </Typography>
        {showDeleteButton && onDelete && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={onDelete}
          >
            İmzayı Kaldır
          </Button>
        )}
      </Box>
      <Box 
        component="img"
        src={signatureUrl}
        alt="İmza"
        sx={{ 
          maxWidth: '100%',
          height: 'auto',
          border: '1px solid #ddd',
          borderRadius: 4,
          display: 'block',
          margin: '0 auto'
        }}
      />
    </Box>
  );
};

export { FileDisplay, SignatureDisplay, formatFileSize, isImageFile };
